import { supabase } from '../lib/supabase';

const callDashboard = async ({ startDate = null, endDate = null } = {}) => {
  const [dashboardResult, adjustmentResult, billingResult, billingRowsResult, collectedResult] = await Promise.all([
    supabase.rpc('get_finance_officer_dashboard', {
      p_start_date: startDate || null,
      p_end_date: endDate || null,
    }),
    // This helper keeps the dashboard compatible with deployments that
    // already applied the original finance migration before corrections were
    // added to the dashboard RPC.
    supabase.rpc('get_finance_billing_adjustments'),
    // Billing requests are kept separate from the ledger so Finance can see
    // references such as BILL-XXXXXXXX even when ledger posting is missing.
    supabase.rpc('get_finance_billing_records'),
    // Direct fallback for databases that have not deployed the billing-record
    // RPC yet. The finance RLS policy added by the finance migration permits
    // this read without exposing billing rows to other roles.
    supabase
      .from('student_billing_requests')
      .select('*')
      .neq('status', 'cancelled')
      .order('requested_at', { ascending: false })
      .limit(5000),
    // Corrections are account credits, not cash received. Use a dedicated
    // total so they cannot inflate the collection metric.
    supabase.rpc('get_finance_collected_total', {
      p_start_date: startDate || null,
      p_end_date: endDate || null,
    }),
  ]);
  if (dashboardResult.error) throw dashboardResult.error;

  const data = dashboardResult.data || {};
  const fallbackAdjustmentData = adjustmentResult.error ? null : adjustmentResult.data;
  const fallbackBillingData = billingResult.error ? null : billingResult.data;
  const directBillingRows = billingRowsResult.error ? [] : (billingRowsResult.data || []);
  const adjustments = Array.isArray(fallbackAdjustmentData?.adjustments)
    ? fallbackAdjustmentData.adjustments
    : (Array.isArray(data.adjustments) ? data.adjustments : []);
  const adjustmentsByOriginalId = new Map();
  const adjustmentLedgerIds = new Set();
  adjustments.forEach(adjustment => {
    const key = String(adjustment.original_ledger_id || '');
    if (key) adjustmentsByOriginalId.set(key, Number(adjustmentsByOriginalId.get(key) || 0) + (Number(adjustment.amount) || 0));
    if (adjustment.adjustment_ledger_id) adjustmentLedgerIds.add(String(adjustment.adjustment_ledger_id));
  });
  const rawTransactions = Array.isArray(data.transactions) ? data.transactions : [];
  const splitPenaltyReferences = new Set(rawTransactions
    .filter(transaction => transaction.reference_id && /late reporting penalty|late unit registration penalty/i.test(String(transaction.description || '')))
    .map(transaction => String(transaction.reference_id)));
  const legacyAggregateDuplicates = rawTransactions.filter(transaction =>
    transaction.reference_id
    && splitPenaltyReferences.has(String(transaction.reference_id))
    && /late registration penalty|late reporting \/ registration penalty/i.test(String(transaction.description || ''))
  );
  const legacyDuplicateDebit = legacyAggregateDuplicates.reduce((sum, transaction) => sum + (Number(transaction.debit_amount) || 0), 0);
  const transactions = rawTransactions
    .filter(transaction => !legacyAggregateDuplicates.includes(transaction))
    .map(transaction => {
    const adjustedAmount = Number(adjustmentsByOriginalId.get(String(transaction.transaction_id)) ?? transaction.adjusted_amount ?? 0) || 0;
    return {
      ...transaction,
      is_adjustment_transaction: Boolean(transaction.adjustment_id || adjustmentLedgerIds.has(String(transaction.transaction_id))),
      adjusted_amount: adjustedAmount,
      remaining_amount: Number(transaction.debit_amount) > 0
        ? Math.max((Number(transaction.debit_amount) || 0) - adjustedAmount, 0)
        : 0,
    };
  });
  const adjustmentCreditsInView = transactions.reduce((total, transaction) => {
    const isAdjustment = transaction.is_adjustment_transaction
      || transaction.adjustment_id
      || transaction.original_ledger_id
      || String(transaction.doc_no || '').toUpperCase().startsWith('ADJ-');
    return isAdjustment ? total + (Number(transaction.credit_amount) || 0) : total;
  }, 0);
  const transactionCollectedTotal = transactions.reduce((total, transaction) => {
    const isAdjustment = transaction.is_adjustment_transaction
      || transaction.adjustment_id
      || transaction.original_ledger_id
      || String(transaction.doc_no || '').toUpperCase().startsWith('ADJ-');
    return isAdjustment ? total : total + (Number(transaction.credit_amount) || 0);
  }, 0);
  const rawCollectedTotal = Number(data.metrics?.total_credits) || 0;
  const fallbackCollectedTotal = rawCollectedTotal > 0
    ? Math.max(rawCollectedTotal - adjustmentCreditsInView, 0)
    : transactionCollectedTotal;
  const collectedTotal = collectedResult.error
    ? fallbackCollectedTotal
    : Number(collectedResult.data) || 0;
  const studentById = new Map((Array.isArray(data?.students) ? data.students : [])
    .map(student => [String(student.id), student]));
  const ledgerReferenceIds = new Set(transactions
    .map(transaction => transaction.reference_id)
    .filter(Boolean)
    .map(reference => String(reference)));
  const directBillingQueue = directBillingRows.map(item => {
    const student = studentById.get(String(item.student_id));
    const reportingPenalty = Number(item.late_reporting_fee) || 0;
    const unitRegistrationPenalty = Number(item.late_unit_registration_fee) || 0;
    return {
      ...item,
      finance_reference: item.finance_reference
        || (item.id ? `BILL-${String(item.id).slice(0, 8).toUpperCase()}` : null),
      ledger_posted: ledgerReferenceIds.has(String(item.id)),
      student_name: item.student_name || student?.student_name || null,
      student_number: item.student_number || student?.student_number || null,
      base_tuition_fee: Number(item.base_tuition_fee) || 0,
      summative_exam_fee: Number(item.summative_exam_fee) || 0,
      statutory_fee: Number(item.statutory_fee) || 0,
      school_fee: Number(item.school_fee) || 0,
      // The legacy late_registration_fee aggregate is intentionally ignored.
      // Reporting and unit-registration penalties are separate charges.
      late_reporting_fee: reportingPenalty,
      late_unit_registration_fee: unitRegistrationPenalty,
      reactivation_fee: Number(item.reactivation_fee) || 0,
      penalty_total: reportingPenalty + unitRegistrationPenalty + (Number(item.reactivation_fee) || 0),
      total_amount: Number(item.total_amount) || 0,
    };
  });
  const metrics = {
    ...(data.metrics || {}),
    total_debits: Math.max((Number(data.metrics?.total_debits) || 0) - legacyDuplicateDebit, 0),
    outstanding_balance: Math.max((Number(data.metrics?.outstanding_balance) || 0) - legacyDuplicateDebit, 0),
    total_credits: collectedTotal,
    adjustment_count: Number(fallbackAdjustmentData?.adjustment_count ?? data.metrics?.adjustment_count ?? adjustments.length) || 0,
    adjusted_amount: Number(fallbackAdjustmentData?.adjusted_amount ?? data.metrics?.adjusted_amount ?? adjustments.reduce((sum, item) => sum + (Number(item.amount) || 0), 0)) || 0,
  };
  const preferredBillingQueue = Array.isArray(fallbackBillingData?.billing_records)
    ? fallbackBillingData.billing_records
    : (Array.isArray(data?.billing_queue) ? data.billing_queue : []);
  const preferredIds = new Set(preferredBillingQueue.map(item => String(item.id)));
  const rawBillingQueue = preferredBillingQueue.length
    ? [...preferredBillingQueue, ...directBillingQueue.filter(item => !preferredIds.has(String(item.id)))]
    : directBillingQueue;
  const billingQueue = rawBillingQueue.map(item => {
    const baseTuition = Number(item.base_tuition_fee) || 0;
    const examFee = Number(item.summative_exam_fee) || 0;
    const statutoryFee = Number(item.statutory_fee) || 0;
    const schoolFee = Number(item.school_fee) || 0;
    const lateReportingFee = Number(item.late_reporting_fee) || 0;
    const lateUnitRegistrationFee = Number(item.late_unit_registration_fee) || 0;
    const reactivationFee = Number(item.reactivation_fee) || 0;
    const penaltyTotal = lateReportingFee + lateUnitRegistrationFee + reactivationFee;
    const componentTotal = baseTuition + examFee + statutoryFee + schoolFee + penaltyTotal;
    const legacyPenalty = Number(item.late_registration_fee) || 0;
    const rawTotal = Number(item.total_amount) || 0;
    // If an older response still includes the aggregate in total_amount,
    // subtract it once. Never expose the aggregate as a separate charge.
    const totalAmount = rawTotal > 0
      ? (legacyPenalty > 0 && rawTotal > componentTotal
        ? Math.max(rawTotal - legacyPenalty, 0)
        : rawTotal)
      : componentTotal;
    return {
      ...item,
      // Keep the same stable reference format used by the student statement
      // when a legacy row has not populated finance_reference yet.
      finance_reference: item.finance_reference
        || (item.id ? `BILL-${String(item.id).slice(0, 8).toUpperCase()}` : null),
      base_tuition_fee: baseTuition,
      summative_exam_fee: examFee,
      statutory_fee: statutoryFee,
      school_fee: schoolFee,
      late_reporting_fee: lateReportingFee,
      late_unit_registration_fee: lateUnitRegistrationFee,
      reactivation_fee: reactivationFee,
      penalty_total: penaltyTotal,
      // Older billing rows may have a zero/NULL aggregate even though their
      // component charges are present. Keep the dashboard total useful.
      total_amount: totalAmount,
    };
  });

  return {
    metrics,
    transactions,
    payments: Array.isArray(data?.payments) ? data.payments : [],
    adjustments,
    billingQueue,
    students: Array.isArray(data?.students) ? data.students : [],
  };
};

const createBillingAdjustment = async ({ originalLedgerId, amount, adjustmentType, reason, approvalReference }) => {
  const { data, error } = await supabase.rpc('create_finance_billing_adjustment', {
    p_original_ledger_id: originalLedgerId,
    p_amount: Number(amount),
    p_adjustment_type: adjustmentType,
    p_reason: reason,
    p_approval_reference: approvalReference || null,
  });
  if (error) {
    // PostgREST reports a missing/ungranted function as PGRST202. Surface an
    // actionable message instead of exposing the generated schema signature.
    if (error.code === 'PGRST202' || /could not find the function/i.test(error.message || '')) {
      throw new Error('Billing corrections are not enabled in the database. Apply migration 20260101000053, then retry.');
    }
    throw error;
  }
  return data;
};

const recordPayment = async ({ studentId, amount, paymentMethod, receiptNumber, paymentDate, notes }) => {
  const { data, error } = await supabase.rpc('record_finance_payment', {
    p_student_id: studentId,
    p_amount: Number(amount),
    p_payment_method: paymentMethod,
    p_receipt_number: receiptNumber || null,
    p_payment_date: paymentDate ? new Date(paymentDate).toISOString() : new Date().toISOString(),
    p_notes: notes || null,
  });
  if (error) throw error;
  return data;
};

const reconcilePayment = async ({ paymentId, status, reference, notes }) => {
  const { data, error } = await supabase.rpc('reconcile_finance_payment', {
    p_payment_id: paymentId,
    p_reconciliation_status: status,
    p_reconciliation_reference: reference || null,
    p_notes: notes || null,
  });
  if (error) throw error;
  return data;
};

const getStatusLabel = status => String(status || 'unreconciled')
  .replaceAll('_', ' ')
  .replace(/\b\w/g, character => character.toUpperCase());

export const financeOfficerService = {
  getDashboard: callDashboard,
  createBillingAdjustment,
  recordPayment,
  reconcilePayment,
  getStatusLabel,
};

export default financeOfficerService;
